import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from "react";
import api from "@/api/client";
import { devLog } from "@/lib/devLog";

const AuthCtx = createContext(null);

// User profile is kept in sessionStorage (non-sensitive display info only).
// Auth is enforced by httpOnly cookies set by the backend — never stored in JS.
const USER_KEY = "ddd_user";

function readCachedUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user) {
  try {
    if (user) sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    else sessionStorage.removeItem(USER_KEY);
  } catch (e) { devLog.warn("[auth] session cache write failed", e); }
}

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(readCachedUser);
  const [loading, setLoading] = useState(false);
  const bootstrappedRef = useRef(false);

  const setUser = useCallback((u) => {
    setUserState(u);
    writeCachedUser(u);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Staff 2FA — return challenge instead of user so the UI can render the OTP step.
    if (data?.requires_2fa) {
      return { requires_2fa: true, ...data };
    }
    setUser(data.user);
    return data.user;
  }, [setUser]);

  const verify2fa = useCallback(async (challenge_id, otp) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_id, otp });
    setUser(data.user);
    return data.user;
  }, [setUser]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data.user);
    return data.user;
  }, [setUser]);

  const logout = useCallback(async () => {
    // Capture role BEFORE clearing state so we can pick the correct login URL.
    const roleAtLogout = user?.role;
    try { await api.post("/auth/logout"); } catch (e) { devLog.warn("[auth] logout request failed (proceeding to clear local state)", e); }
    setUser(null);
    // Staff / driver → private staff URL. Customer → public /login.
    window.location.href = roleAtLogout === "customer" ? "/login" : "/ops-console-fx-9k2m4";
  }, [setUser, user]);

  useEffect(() => {
    // Silent bootstrap: verify session via httpOnly cookie exactly once per mount.
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (!readCachedUser()) return;
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, [setUser]);

  const ctxValue = useMemo(
    () => ({ user, setUser, login, verify2fa, register, logout, loading, setLoading }),
    [user, setUser, login, verify2fa, register, logout, loading],
  );

  return (
    <AuthCtx.Provider value={ctxValue}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

/** Roles allowed to delete business entities (fleet, compliance, fuel logs, service records, documents, products).
 *  User-account deletion remains admin-only for security and is not gated by this hook. */
export const DELETE_ROLES = ["admin", "accounts", "manager"];

export function useCanDelete() {
  const { user } = useAuth();
  return DELETE_ROLES.includes(user?.role);
}
